import React, { useCallback } from "react"

import { atom, selector, useRecoilState, useRecoilValue } from "recoil"

import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import {
  useConfirmSummaryMutation,
  useFindProposalStatusesQuery,
} from "~/graphql/client"
import type { CreateProposalStatusInput, ProposalStatus } from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"

import { useProposalContext } from "./proposal-context"
import { proposalLocalState } from "./proposal-state"

const proposalLocalStatusArrayState = atom<ProposalStatus[]>({
  key: `proposalLocalStatusArray`,
  default: [],
})

const summaryIsConfirmedState = selector<boolean>({
  key: `summaryIsConfirmed`,
  get: ({ get }) =>
    get(proposalLocalStatusArrayState).some(
      (status) => status.status === `summary_confirmed`,
    ) || get(proposalLocalState).currentStatus === `summary_confirmed`,
})

export const useProposalStatuses = (): {
  statusArray: ProposalStatus[]
  refetch: () => void
  currentStatus: string | null
  summaryIsConfirmed: boolean
  isLoading: boolean
} => {
  const { client } = useGraphqlClient()
  const proposal = useProposalContext()
  const summaryIsConfirmed = useRecoilValue(summaryIsConfirmedState)
  const [statusArray, setStatusArray] = useRecoilState(
    proposalLocalStatusArrayState,
  )
  const onError = useDispatchError()

  const { refetch, isLoading } = useFindProposalStatusesQuery(
    client,
    { proposalId: proposal.remote.id },
    {
      enabled: !!proposal.remote.id,
      staleTime: Infinity,
      onSuccess: ({ getProposalStatusEvents }) =>
        setStatusArray(getProposalStatusEvents),
      onError,
    },
  )

  return {
    statusArray,
    isLoading,
    refetch,
    summaryIsConfirmed,
    currentStatus: proposal.local.currentStatus,
  }
}

export const useConfirmSummary = (): {
  confirmSummary: () => Promise<void>
} => {
  const { client } = useGraphqlClient()
  const proposal = useProposalContext().remote
  const onError = useDispatchError()
  const { refetch } = useProposalStatuses()

  const mutation = useConfirmSummaryMutation(client, { onError })

  const confirmSummary = useCallback(async () => {
    const createProposalStatusInput: CreateProposalStatusInput = {
      proposalId: proposal.id,
      message: `Summary Confirmed`,
    }

    await mutation.mutateAsync({ createProposalStatusInput })

    refetch()
  }, [proposal.id, mutation, refetch])

  return { confirmSummary }
}
